import { Navigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

interface RootState {
  user: { role: string; userId: string };
}

interface ProfileAccessGuardProps {
  /**
   * Roles allowed to view this sub-page when it belongs to ANOTHER user.
   * The owner can always view their own page regardless of this list.
   */
  othersAllowedRoles: string[];
  children: React.ReactElement;
}

/**
 * Guards sensitive profile sub-routes (`/profile/:id/...`) where `:id` may be
 * any user. Owners always pass; viewing someone else's sensitive data
 * (payroll, documents) requires one of `othersAllowedRoles`.
 *
 * Closes a direct-URL information-disclosure hole: the profile tab navigation
 * only HID links for unauthorized viewers — the routes themselves still
 * rendered another employee's salary/documents to anyone who typed the URL.
 */
export const ProfileAccessGuard = ({ othersAllowedRoles, children }: ProfileAccessGuardProps) => {
  const { id } = useParams();
  const role = useSelector((state: RootState) => state.user.role);
  const userId = useSelector((state: RootState) => state.user.userId);

  const isOwnProfile = !id || id === userId;
  if (isOwnProfile) return children;

  if (!othersAllowedRoles.includes(role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};
